import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AccessibilityIcon, ArrowLeftIcon, ArrowRightIcon, AwardIcon, BanknoteIcon, CalendarClockIcon, CheckIcon, ClockIcon, InfoIcon, MailIcon, MapPinIcon, MonitorPlayIcon, PhoneIcon, UsersIcon } from 'lucide-react';
import { formations, getFormation } from '../data/formations';
import { site, afficherTarifs } from '../data/site';
import { useSeo } from '../hooks/useSeo';
export function FormationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const formation = slug ? getFormation(slug) : undefined;
  useSeo({
    title: formation ? `${formation.titre} — Formation à Joigny (Yonne) | Zillenium Academy` : 'Formation — Zillenium Academy',
    description: formation ? `${formation.promesse} Durée : ${formation.duree}. Format : ${formation.format}. Organisme de formation certifié Qualiopi à Joigny (89).` : 'Formation professionnelle à Joigny (Yonne).'
  });
  if (!formation) {
    return <Navigate to="/formations" replace data-id="element-239" data-mp="1oa5l6l4finz1" />;
  }
  const autres = formations.filter(f => f.slug !== formation.slug);
  const infos = [{
    icon: ClockIcon,
    label: 'Durée',
    value: formation.duree
  }, {
    icon: MonitorPlayIcon,
    label: 'Format',
    value: formation.format
  }, {
    icon: BanknoteIcon,
    label: 'Tarif',
    value: afficherTarifs ? formation.tarif : 'Sur devis'
  }, {
    icon: CalendarClockIcon,
    label: 'Délai d’accès',
    value: formation.delaiAcces
  }];
  return <>
      <section className="bg-brand-900" data-id="element-240" data-mp="2bzgc7avz93zz">
        <div className="mx-auto max-w-content px-5 py-12 lg:px-8 lg:py-16" data-id="element-241" data-mp="2c2m38vo62fyc">
          <Link to="/formations" className="inline-flex items-center gap-2 text-sm font-medium text-brand-200 transition-colors duration-150 ease-out hover:text-white" data-id="element-242" data-mp="1y94ix8nnoz79">
            <ArrowLeftIcon size={16} aria-hidden data-id="element-243" data-mp="2pyfy4as3v4sg" />
            Toutes nos formations
          </Link>
          <span className="mt-6 inline-block rounded-md bg-white/10 px-3 py-1 text-xs font-semibold text-accent-400" data-id="element-244" data-mp="2utdj8g2nt4lp">
            {formation.categorie}
          </span>
          <h1 className="mt-4 max-w-4xl text-3xl font-extrabold leading-[1.12] text-white md:text-[46px]" data-id="element-245" data-mp="30mz446aj4yvv">
            {formation.titre}
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-brand-200" data-id="element-246" data-mp="2qngn9ypx1t81">
            {formation.promesse}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-247" data-mp="2lon69wbf7k34">
            <Link to={`/contact?formation=${formation.slug}&objet=devis`} className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-500 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-accent-600" data-id="element-248" data-mp="2gxopywqfmlzv">
              Demander un devis
              <ArrowRightIcon size={18} aria-hidden data-id="element-249" data-mp="22wue2446e2wg" />
            </Link>
            <Link to={`/contact?formation=${formation.slug}&objet=rappel`} className="inline-flex items-center justify-center rounded-lg border border-white/25 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-250" data-mp="1xz2qtneh74yl">
              Être recontacté
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-content gap-12 px-5 py-14 lg:grid-cols-[1.6fr_1fr] lg:px-8" data-id="element-251" data-mp="22rv7s392jjtw">
        <div data-id="element-252" data-mp="154wfs5tbn02c">
          <img src={formation.image} alt="" aria-hidden className="aspect-[16/7] w-full rounded-2xl object-cover" data-id="element-253" data-mp="1555yr9fy86yj" />

          <section className="mt-10" data-id="element-254" data-mp="1f2u22nrmemsb">
            <h2 className="text-2xl font-bold text-ink" data-id="element-255" data-mp="1f1sgvqgw4jwv">Présentation</h2>
            <p className="mt-3 text-[17px] leading-relaxed text-ink-muted" data-id="element-256" data-mp="1jtsirogitmw3">
              {formation.presentation}
            </p>
          </section>

          <section className="mt-10" data-id="element-257" data-mp="mbsjsdsmsb4k">
            <h2 className="text-2xl font-bold text-ink" data-id="element-258" data-mp="rcg8utxauhw5">À qui s’adresse la formation ?</h2>
            <p className="mt-3 text-[17px] leading-relaxed text-ink-muted" data-id="element-259" data-mp="2t1vv7bcibx3o">
              {formation.publicConcerne}
            </p>
          </section>

          <section className="mt-10" data-id="element-260" data-mp="2o4ncn9ng5r1w">
            <h2 className="text-2xl font-bold text-ink" data-id="element-261" data-mp="32ryn187w750f">Objectifs pédagogiques</h2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-262" data-mp="32truuqu8rva0">
              {formation.objectifs.map(o => <li key={o} className="flex gap-3 text-[15px] leading-relaxed text-ink-muted" data-id="element-263" data-mp="3bsn1u55419ed">
                  <CheckIcon size={18} className="mt-0.5 shrink-0 text-accent-500" aria-hidden data-id="element-264" data-mp="3buhktdwtmrft" />
                  {o}
                </li>)}
            </ul>
          </section>

          <section className="mt-12" data-id="element-265" data-mp="3logmycwwab7r">
            <h2 className="text-2xl font-bold text-ink" data-id="element-266" data-mp="3grgfpyob72f2">Programme</h2>
            <ol className="mt-6 divide-y divide-surface-line border-y border-surface-line" data-id="element-267" data-mp="1rcs4w1vd9sj5">
              {formation.programme.map((p, i) => <li key={p} className="flex items-start gap-4 py-4" data-id="element-268" data-mp="1meqj396k3f3z" data-mpi={i}>
                  <span className="text-sm font-bold text-accent-500" data-id="element-269" data-mp="1v4nksdjv8zk">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[16px] leading-relaxed text-ink" data-id="element-270" data-mp="6tzgin58edxe">{p}</span>
                </li>)}
            </ol>
          </section>

          <section className="mt-12 grid gap-8 sm:grid-cols-2" data-id="element-271" data-mp="3t9tvkascukqr">
            <div data-id="element-272" data-mp="3t7zamwh32smo">
              <h2 className="text-lg font-bold text-ink" data-id="element-273" data-mp="3faua1c052lyt">Prérequis</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-274" data-mp="3fcnjez24zkai">
                {formation.prerequis}
              </p>
            </div>
            <div data-id="element-275" data-mp="35d6g2oacxihp">
              <h2 className="text-lg font-bold text-ink" data-id="element-276" data-mp="35doglx41yivf">Lieu et format</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-277" data-mp="3aaoe3jmu6dax">
                {formation.format} — {formation.lieu}.
              </p>
            </div>
            <div data-id="element-278" data-mp="18k4jg5j1dd1r">
              <h2 className="text-lg font-bold text-ink" data-id="element-279" data-mp="18kmq7w3br9nx">Méthodes pédagogiques</h2>
              <ul className="mt-2 flex flex-wrap gap-2" data-id="element-280" data-mp="172xtn1hu0qrr">
                {formation.methodes.map(m => <li key={m} className="rounded-md bg-surface-soft px-2.5 py-1 text-sm font-medium text-brand-800" data-id="element-281" data-mp="1244d10790jnd">
                    {m}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-282" data-mp="t5ixzf0zjmrc">
              <h2 className="text-lg font-bold text-ink" data-id="element-283" data-mp="1pt5sh7ei0v25">Modalités d’évaluation</h2>
              <ul className="mt-2 flex flex-wrap gap-2" data-id="element-284" data-mp="1pv9vzo8hjs1w">
                {formation.evaluation.map(e => <li key={e} className="rounded-md bg-surface-soft px-2.5 py-1 text-sm font-medium text-brand-800" data-id="element-285" data-mp="1g3du023a3hse">
                    {e}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-286" data-mp="28m1at5b8sano">
              <h2 className="text-lg font-bold text-ink" data-id="element-287" data-mp="23t86ujnll6cn">Tarif</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-288" data-mp="23rx3gky2e0m2">
                {afficherTarifs ? `${formation.tarif}.` : 'Tarif communiqué dans le devis, après analyse de votre besoin (nombre de participants, format et lieu retenus).'}
              </p>
            </div>
            <div data-id="element-289" data-mp="3vizbntk5mtrz">
              <h2 className="text-lg font-bold text-ink" data-id="element-290" data-mp="4a3bvha57t74">Modalités d’accès</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-291" data-mp="47z42o94ia1k">
                Un échange préalable permet d’analyser le besoin. L’inscription est validée après
                accord sur le programme, le devis et les documents contractuels.
              </p>
            </div>
            <div data-id="element-292" data-mp="j1lqpbr9nhow">
              <h2 className="text-lg font-bold text-ink" data-id="element-293" data-mp="n0l5do2sustj">Délai d’accès</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-294" data-mp="n2xhhklvm8yo">
                {formation.delaiAcces}.
              </p>
            </div>
            <div className="sm:col-span-2" data-id="element-295" data-mp="x028pyukwumc">
              <h2 className="flex items-center gap-2 text-lg font-bold text-ink" data-id="element-296" data-mp="wwwl6o1lm0sq">
                <AwardIcon size={18} className="text-accent-500" aria-hidden data-id="element-297" data-mp="7u0n2i78mjs3" />
                Sanction de la formation
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-298" data-mp="gw9wf4qrs73w">
                {formation.sanction} Sont également remis les émargements et un questionnaire de
                satisfaction.
              </p>
            </div>
            <div className="sm:col-span-2 rounded-xl border border-surface-line bg-surface-soft p-6" data-id="element-299" data-mp="3lbjhckg6en0x">
              <h2 className="flex items-center gap-2 text-lg font-bold text-ink" data-id="element-300" data-mp="3gcydmb1ptl7q">
                <AccessibilityIcon size={18} className="text-brand-700" aria-hidden data-id="element-301" data-mp="3u3bqmrtgrq3x" />
                Accessibilité aux personnes en situation de handicap
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-302" data-mp="31missxd7ofa3">
                Zillenium Academy étudie les aménagements possibles en fonction des besoins du
                bénéficiaire. Contactez notre référent handicap avant l’inscription :{' '}
                <a href={`mailto:${site.refHandicap}`} className="font-semibold text-brand-800 hover:underline" data-id="element-303" data-mp="2nfxjp6d743k6">
                  {site.refHandicap}
                </a>
                .
              </p>
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-304" data-mp="2ig1fb60l6a43">
          <div className="rounded-xl border border-surface-line bg-white p-6 shadow-card" data-id="element-305" data-mp="3691krcsqtiot">
            <h2 className="text-lg font-bold text-ink" data-id="element-306" data-mp="3b5s01xwq4a96">L’essentiel</h2>
            <dl className="mt-4 divide-y divide-surface-line" data-id="element-307" data-mp="2w9sqsjxvo42g">
              {infos.map(info => <div key={info.label} className="flex items-start justify-between gap-4 py-3" data-id="element-308" data-mp="31842z1bnaqnu">
                  <dt className="inline-flex items-center gap-2 text-sm text-ink-muted" data-id="element-309" data-mp="1kunfwsnlez7r">
                    <info.icon size={15} className="text-accent-500" aria-hidden data-id="element-310" data-mp="1ptgoc6o0w012" />
                    {info.label}
                  </dt>
                  <dd className="text-right text-sm font-semibold text-ink" data-id="element-311" data-mp="2x6klo0tig2pa">{info.value}</dd>
                </div>)}
              <div className="flex items-start justify-between gap-4 py-3" data-id="element-312" data-mp="323uk25yvnop9">
                <dt className="inline-flex items-center gap-2 text-sm text-ink-muted" data-id="element-313" data-mp="2neybm7sws9o4">
                  <UsersIcon size={15} className="text-accent-500" aria-hidden data-id="element-314" data-mp="2sdrg5aq7wqc5" />
                  Prérequis
                </dt>
                <dd className="text-right text-sm font-semibold text-ink" data-id="element-315" data-mp="2edqe3ry7vcv3">{formation.prerequis}</dd>
              </div>
            </dl>

            {formation.dureeNote && <p className="mt-4 flex gap-2 rounded-lg bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800" data-id="element-316" data-mp="24i5gs743f0ro">
                <InfoIcon size={15} className="mt-0.5 shrink-0" aria-hidden data-id="element-317" data-mp="29g70mu9cf447" />
                {formation.dureeNote}
              </p>}

            <Link to={`/contact?formation=${formation.slug}&objet=devis`} className="mt-6 block rounded-lg bg-accent-500 px-5 py-3.5 text-center text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-accent-600" data-id="element-318" data-mp="7obv6wdojvuf">
              Demander un devis
            </Link>
            <Link to={`/contact?formation=${formation.slug}&objet=rappel`} className="mt-3 block rounded-lg border border-surface-line px-5 py-3.5 text-center text-base font-semibold text-brand-800 transition-colors duration-150 ease-out hover:bg-surface-soft" data-id="element-319" data-mp="21x7ggme258js">
              Être recontacté
            </Link>

            <div className="mt-6 space-y-2.5 border-t border-surface-line pt-5 text-sm" data-id="element-320" data-mp="2kva3wwqyixfh">
              <h3 className="font-bold text-ink" data-id="element-321" data-mp="2ksxiuapwk6y7">Contact</h3>
              <p className="flex items-start gap-2 text-ink-muted" data-id="element-322" data-mp="2fwqdok5byzhb">
                <MapPinIcon size={15} className="mt-0.5 shrink-0 text-accent-500" aria-hidden data-id="element-323" data-mp="qgh6qtafdylf" />
                {site.nom}, {site.adresse}
              </p>
              <a href={`mailto:${site.email}`} className="flex items-center gap-2 text-ink-muted hover:text-brand-800" data-id="element-324" data-mp="lka7fjcjblcg">
                <MailIcon size={15} className="text-accent-500" aria-hidden data-id="element-325" data-mp="2qkbd8c38ju4v" />
                {site.email}
              </a>
              <a href={`tel:${site.telephoneLien}`} className="flex items-center gap-2 text-ink-muted hover:text-brand-800" data-id="element-326" data-mp="2un7zccrzi8dk">
                <PhoneIcon size={15} className="text-accent-500" aria-hidden data-id="element-327" data-mp="34ii3b61y0ptz" />
                {site.telephone}
              </a>
            </div>
          </div>
        </aside>
      </div>

      <section className="border-t border-surface-line bg-surface-soft" data-id="element-328" data-mp="3ea5posjn7gol">
        <div className="mx-auto max-w-content px-5 py-14 lg:px-8" data-id="element-329" data-mp="3ib7pbahh1ur3">
          <h2 className="text-2xl font-bold text-ink" data-id="element-330" data-mp="3icsmz8z31tt6">Nos autres formations</h2>
          <ul className="mt-6 grid gap-4 md:grid-cols-3" data-id="element-331" data-mp="3nblzay2mverr">
            {autres.map(f => <li key={f.slug} data-id="element-332" data-mp="3s9do3j7zekrq">
                <Link to={`/formations/${f.slug}`} className="flex h-full flex-col justify-between gap-4 rounded-xl border border-surface-line bg-white p-5 transition-colors duration-150 ease-out hover:border-accent-400" data-id="element-333" data-mp="3s6j9477qls5p">
                  <div data-id="element-334" data-mp="3sewixjn8uqha">
                    <span className="text-xs font-semibold text-accent-600" data-id="element-335" data-mp="3od189ojfb0qu">{f.categorie}</span>
                    <h3 className="mt-1.5 font-bold leading-snug text-ink" data-id="element-336" data-mp="3ofdvoh7ya3ba">{f.titre}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-800" data-id="element-337" data-mp="jz33l84nfl1t">
                    Découvrir
                    <ArrowRightIcon size={15} aria-hidden data-id="element-338" data-mp="f6s3o59wrz99" />
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}